import { useEffect, useMemo, useRef, useState } from "react";
import { appstr } from "@coreapp/utils/appstr";
import {
  Badge,
  Button,
  Flex,
  type ListItemOption,
  Menu,
  Popover,
  Search,
  Text,
  ToolIcon,
  type OpenCtrlRef
} from "@theorgchartdev/storybook";
import { GetWSMgr } from "@/Utils/StateLib";
import { chartModeTestId } from "@/appComponents/Navigation/RightSidebar/Utils/chartModeTestId";
import { enumHierarchyTypes } from "@coreapp/constants";
import { DoBoxLayoutEditor } from "@/appComponents/Navigation/ContextualToolbar/ToolbarOptions";
import BoxStyleSummary from "./BoxStyleSummary";
import BoxStyleFilters, { useFilterBoxStyles } from "./BoxStyleFilters";
import { useSortBoxStyles } from "./useSortBoxStyles";
import {
  buildEditedOrder,
  partitionByEditedOrder,
  type BoxStyleEntry,
  type EditedOrderId
} from "./editedOrderUtils";
import { PendingEditsBanner } from "./PendingEditsBanner";
import { QBoxLayout } from "@coreapp/CoreApp/QBoxLayout";
import { SaveBoxStyles } from "@/Utils/Promises";
import { useRightSidebarContext } from "@/providers/RightSidebarProvider";
import { useToast, TOAST_STATUS } from "@/providers/ToastProvider";
import { useTopic } from "@/hooks/useTopic";
import type { QXDoc } from "@coreapp/CoreApp/QXDoc";
import { createBoldToastMessage } from "@/appComponents/Blocks/Cards/ToastContainer/utils";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";

const HIGHLIGHT_DURATION = 5000; // Duration to keep the box style highlighted after scrolling into view (in milliseconds)

interface BoxStylesProps {
  canUpdateViews: boolean;
}

const BoxStyles = ({ canUpdateViews }: BoxStylesProps) => {
  const wsMgr = GetWSMgr();
  const bsMgr = wsMgr?.GetBoxStylesMgr();
  const ctMgr = wsMgr?.GetChartTemplatesMgr();
  const doc = wsMgr?.GetDoc();

  const filterCtrlRef = useRef<OpenCtrlRef>(null);
  const pendingRafRef = useRef<number | null>(null);
  const highlightTimeoutRef = useRef<number | null>(null);
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [highlightedId, setHighlightedId] = useState<string | null>(null);

  // Using state variables here so we can control and limit the number of re-renders when box styles data changes,
  // instead of directly calling GetBoxStylesMgr methods in the render which causes multiple calls and re-renders due to the way QBoxStylesMgr is implemented.
  // This also allows us to ensure the data is fresh when we need to access it for operations like sorting and filtering.
  const [boxStyleCounts, setBoxStyleCounts] = useState<Map<string, number>>(
    () => ctMgr?.GetBoxStyleUsageCounts() ?? new Map<string, number>()
  );
  const [boxStyles, setBoxStyles] = useState<BoxStyleEntry[]>(
    () =>
      bsMgr?.GetVisibleLayoutsAllSets(
        doc?.IsPositionDocument() ? enumHierarchyTypes.positions : enumHierarchyTypes.employees
      ) ?? []
  );
  const [isPositionChart, setIsPositionChart] = useState(() => doc?.IsPositionDocument() ?? false);
  // Drives previewVersion so cached BoxStyleSummary previews invalidate on each draft mutation.
  const [pendingEditTick, setPendingEditTick] = useState(0);

  const currentEditingId = wsMgr?.GetBLELayout?.()?.GetBoxStyleID() ?? null;
  const pendingEditCount = wsMgr?.GetPendingEditCount?.() ?? 0;
  const tombstoneCount = bsMgr?.GetTombstoneCount?.() ?? 0;
  // Search/filter never hides the box being edited or any box with a pending draft.
  const alwaysShowIds = new Set<EditedOrderId>(bsMgr?.GetAllEditDrafts().map(d => d.id) ?? []);
  if (currentEditingId != null) alwaysShowIds.add(currentEditingId);

  const { navigateWithBackTarget } = useRightSidebarContext();
  const { addToast }: any = useToast();
  const { canAccessGrouping } = usePermissionCheck();

  const filterStorageKey = `box-styles-filters-${doc?.GetChartID?.() ?? "default"}`;

  const {
    filteredBoxStyles,
    filtersAreDefault,
    searchProps,
    activeFilters,
    toggleFilter,
    onResetFilters
  } = useFilterBoxStyles(
    boxStyles,
    boxStyleCounts,
    isPositionChart,
    canAccessGrouping,
    filterStorageKey,
    alwaysShowIds
  );
  const { sortedBoxStyles, sortOptions, sortBy } = useSortBoxStyles(
    filteredBoxStyles,
    boxStyleCounts,
    isPositionChart,
    canAccessGrouping
  );
  const draftOrder = useMemo<EditedOrderId[]>(
    () => (bsMgr ? bsMgr.GetAllEditDrafts().map(d => d.id) : []),
    [bsMgr, pendingEditTick]
  );
  const editedOrder = useMemo(
    () => buildEditedOrder(currentEditingId, draftOrder),
    [currentEditingId, draftOrder]
  );
  const displayBoxStyles = useMemo(
    () => partitionByEditedOrder(sortedBoxStyles, editedOrder),
    [sortedBoxStyles, editedOrder]
  );

  const hierarchyType = isPositionChart
    ? enumHierarchyTypes.positions
    : enumHierarchyTypes.employees;

  const refreshBoxStylesData = () => {
    if (!ctMgr || !bsMgr) return;
    setBoxStyleCounts(ctMgr.GetBoxStyleUsageCounts());
    setBoxStyles(bsMgr.GetVisibleLayoutsAllSets(hierarchyType));
  };

  const handleViewNavigate = () => {
    navigateWithBackTarget("view", {
      mode: "boxlibrary",
      title: appstr("Back to box styles")
    });
  };

  const handleRename = async (layout: QBoxLayout, newName: string) => {
    if (!bsMgr) return;

    const id = layout.GetBoxStyleID();
    const ble = wsMgr?.GetBLELayout?.();
    const bleMatches = ble?.GetBoxStyleID?.() === id;

    if (wsMgr?.fLayoutPanel?.EditBoxStyle?.() === true) {
      // BLE: route the rename through the draft so the list layout stays pristine for discard.
      if (bleMatches && ble) {
        ble.SetBoxStyleName(newName);
        bsMgr.StageEditDraft(id, ble);
      } else {
        const isNew = bsMgr.HasNewDraftId(id);
        const draftLayout =
          bsMgr.GetEditDraft(id)?.layout ??
          layout.Clone({ includeBoxStyleID: true, includeLayoutName: true });
        draftLayout.SetBoxStyleName(newName);
        bsMgr.StageEditDraft(id, draftLayout, { isNew });
      }
      refreshBoxStylesData();
      return;
    }

    const oldName = layout.GetBoxStyleName();
    const draft = bsMgr.GetEditDraft(id)?.layout;
    try {
      layout.SetBoxStyleName(newName);
      // Inline rename must propagate to in-flight drafts; otherwise BLE save sees a stale snapshot and rolls this back.
      draft?.SetBoxStyleName(newName);
      if (bleMatches) ble?.SetBoxStyleName(newName);
      bsMgr.SetPendingScrollTarget(id);
      const { success } = await SaveBoxStyles();
      if (!success) {
        throw new Error("Failed to save box styles");
      }
      GetWSMgr()?.BoxStylesSaved?.();
      GetWSMgr()?.GetUsageTracker()?.ReportEvent("box_layout_editor.box_style.saved", {
        change_type: "rename"
      });
      refreshBoxStylesData();
      addToast({
        ...createBoldToastMessage("box style changes saved", {
          "box style name": layout.GetBoxStyleName()
        }),
        status: TOAST_STATUS.SUCCESS
      });
    } catch (error) {
      layout.SetBoxStyleName(oldName);
      draft?.SetBoxStyleName(oldName);
      if (bleMatches) ble?.SetBoxStyleName(oldName);
      bsMgr.SetPendingScrollTarget(null);
      refreshBoxStylesData();
      addToast({
        message: appstr("The box style could not be renamed. Please try again."),
        status: TOAST_STATUS.ERROR
      });
    }
  };

  const handleCopy = async (layout: QBoxLayout) => {
    if (!bsMgr) return;

    let addedLayout: QBoxLayout | null = null;
    try {
      const id = layout.GetBoxStyleID();
      const liveSource =
        id === currentEditingId ? wsMgr?.GetBLELayout?.() : bsMgr.GetEditDraft(id)?.layout;
      const source = liveSource ?? layout;
      const newLayout = source.Clone();
      newLayout.SetIsBoxStyle(true);
      // SetIsBoxStyle nulled the cells' live field refs; rebind them so the copy isn't blank.
      doc?.FinishLoadLayout(newLayout);
      const copyName = source.GetBoxStyleName() + " copy";
      addedLayout = bsMgr.AddLayout(newLayout, copyName);
      if (!addedLayout) {
        throw new Error("Failed to create copied box style");
      }
      if (wsMgr?.fLayoutPanel?.EditBoxStyle?.() === true) {
        // BLE: stage the copy as a new-this-session draft; commit persists it, discard drops it.
        bsMgr.StageEditDraft(addedLayout.GetBoxStyleID(), addedLayout, { isNew: true });
        refreshBoxStylesData();
        return;
      }
      bsMgr.SetPendingScrollTarget(addedLayout.GetBoxStyleID());
      const { success } = await SaveBoxStyles();
      if (!success) {
        throw new Error("Failed to save box styles");
      }
      GetWSMgr()?.BoxStylesSaved?.();
      GetWSMgr()?.GetUsageTracker()?.ReportEvent("box_layout_editor.box_style.created");
      refreshBoxStylesData();
      addToast({
        ...createBoldToastMessage("copied to box style library", {
          "box style name": addedLayout.GetBoxStyleName()
        }),
        status: TOAST_STATUS.SUCCESS
      });
    } catch (error) {
      if (addedLayout) {
        bsMgr.RemoveLayout(addedLayout);
      }
      bsMgr.SetPendingScrollTarget(null);
      refreshBoxStylesData();
      addToast({
        message: appstr("The box style could not be copied"),
        status: TOAST_STATUS.ERROR
      });
    }
  };

  const handleDelete = async (layout: QBoxLayout) => {
    if (!bsMgr) return;

    const removedName = layout.GetBoxStyleName();
    const removedId = layout.GetBoxStyleID();

    if (wsMgr?.fLayoutPanel?.EditBoxStyle?.() === true) {
      // Staged copy (isNew) was never persisted, so drop it silently — no tombstone, not counted.
      // A persisted style is tombstoned so discard can re-insert it and commit can persist the delete.
      const setId = bsMgr.GetBoxStyleSetFromID(removedId, true);
      const priorSet = bsMgr.CurrentBoxStyleSet();
      const wasNew = bsMgr.HasNewDraftId(removedId);
      bsMgr.ClearEditDraft(removedId);
      if (!wasNew) bsMgr.AddTombstone(removedId, layout, setId);
      bsMgr.SwitchBoxStyles(setId);
      bsMgr.RemoveLayout(layout);
      bsMgr.SwitchBoxStyles(priorSet);
      bsMgr.NotifyPendingEditsChanged();
      refreshBoxStylesData();
      return;
    }

    let removedLayout: QBoxLayout | null = null;
    const stashedDraft = bsMgr.GetEditDraft(removedId);
    try {
      removedLayout = layout;
      bsMgr.RemoveLayout(layout);
      if (stashedDraft) {
        bsMgr.ClearEditDraft(removedId);
        bsMgr.NotifyPendingEditsChanged();
      }
      const { success } = await SaveBoxStyles();
      if (!success) {
        throw new Error("Failed to save box styles");
      }
      GetWSMgr()?.BoxStylesSaved?.();
      refreshBoxStylesData();
      addToast({
        ...createBoldToastMessage("box style deleted", {
          "box style name": layout.GetBoxStyleName()
        }),
        status: TOAST_STATUS.SUCCESS
      });
    } catch (error) {
      if (removedLayout) {
        const restoredLayout = bsMgr.AddLayout(removedLayout, removedName);
        restoredLayout.SetBoxStyleID(removedId);
        restoredLayout.SetBoxStyleName(removedName);
      }
      if (stashedDraft) {
        bsMgr.SetEditDraft(removedId, stashedDraft.layout, { isNew: stashedDraft.isNew });
        bsMgr.NotifyPendingEditsChanged();
      }
      refreshBoxStylesData();
      addToast({
        message: appstr("The box style could not be deleted"),
        status: TOAST_STATUS.ERROR
      });
    }
  };

  const handleRevert = (layout: QBoxLayout) => {
    if (!bsMgr) return;
    const id = layout.GetBoxStyleID();
    // A staged copy never existed on BLE entry, so reverting drops it entirely; an edited
    // existing style left the list layout pristine, so clearing its draft restores the original.
    if (bsMgr.HasNewDraftId(id)) {
      const setId = bsMgr.GetBoxStyleSetFromID(id, true);
      const priorSet = bsMgr.CurrentBoxStyleSet();
      bsMgr.ClearEditDraft(id);
      bsMgr.SwitchBoxStyles(setId);
      bsMgr.RemoveLayout(layout);
      bsMgr.SwitchBoxStyles(priorSet);
    } else {
      bsMgr.ClearEditDraft(id);
    }
    bsMgr.NotifyPendingEditsChanged();
    refreshBoxStylesData();
  };

  const handleEdit = (layout: QBoxLayout) => {
    if (!doc || !bsMgr) return;

    const id = layout.GetBoxStyleID();
    if (id != null) {
      bsMgr.SetPendingScrollTarget(id);
    }

    const editLayout = bsMgr.CloneLayout(layout, doc);
    editLayout.fBoxStyleID = layout.GetBoxStyleID();
    editLayout.fBoxStyleName = layout.GetBoxStyleName();
    editLayout.SetDocRef(doc);

    const previewObj = doc.GetRootObj()?.Clone(true) ?? null;
    editLayout.SetDBObj(previewObj);
    DoBoxLayoutEditor(editLayout, previewObj, true);
  };

  const handleCreate = (targetHierarchyType: number) => {
    if (!wsMgr) return;
    wsMgr.OpenNewBoxStyleEditor(targetHierarchyType);
  };

  const createOptions: ListItemOption[] = isPositionChart
    ? [
        {
          value: "position-box",
          label: appstr("Position box"),
          dataTestId: chartModeTestId("sidebar", "box-styles-create-position-box"),
          clickHandler: () => handleCreate(enumHierarchyTypes.positions)
        },
        {
          value: "employee-box",
          label: appstr("Employee box"),
          dataTestId: chartModeTestId("sidebar", "box-styles-create-employee-box"),
          clickHandler: () => handleCreate(enumHierarchyTypes.employees)
        }
      ]
    : [
        {
          value: "employee-box",
          label: appstr("Employee box"),
          dataTestId: chartModeTestId("sidebar", "box-styles-create-employee-box"),
          clickHandler: () => handleCreate(enumHierarchyTypes.employees)
        },
        // shouldn't be hit, but adding condition just in case we want to update this to an upgrade chip
        ...(canAccessGrouping
          ? [
              {
                value: "group-box",
                label: appstr("Group box"),
                dataTestId: chartModeTestId("sidebar", "box-styles-create-group-box"),
                clickHandler: () => handleCreate(enumHierarchyTypes.grouping)
              }
            ]
          : [])
      ];

  const sortTrigger = (
    <Badge color="warning" pin="top-right" overlap="square" size={"small"}>
      <ToolIcon name="arrow-up-arrow-down" size="medium-small" />
    </Badge>
  );

  const filterTrigger = (
    <Badge
      border
      color="warning"
      overlap="square"
      pin="top-right"
      showBadge={!filtersAreDefault}
      size={"small"}
    >
      <ToolIcon
        name="filter"
        size="medium-small"
        dataTestId={chartModeTestId("sidebar", "box-styles-filter-btn")}
      />
    </Badge>
  );

  useTopic<QXDoc>("chartDocumentLoaded", newDoc => {
    setIsPositionChart(newDoc.IsPositionDocument());
  });

  useTopic("boxStylesPendingEditsChanged", () => {
    setPendingEditTick(n => n + 1);
    refreshBoxStylesData();
  });

  useTopic("boxStyleNameChanged", () => setPendingEditTick(n => n + 1));

  useTopic("boxStyleDraftMutated", () => setPendingEditTick(n => n + 1));

  useEffect(() => {
    if (!bsMgr) return;
    refreshBoxStylesData();
  }, [hierarchyType, bsMgr]);

  useEffect(() => {
    if (!bsMgr) {
      return;
    }

    if (bsMgr.fPendingScrollTarget == null) {
      return;
    }

    pendingRafRef.current = requestAnimationFrame(() => {
      const targetId = bsMgr.fPendingScrollTarget;
      if (targetId == null) {
        return;
      }

      const target = itemRefs.current[targetId];
      target?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      setHighlightedId(targetId);
      if (highlightTimeoutRef.current != null) {
        clearTimeout(highlightTimeoutRef.current);
      }
      highlightTimeoutRef.current = window.setTimeout(() => {
        setHighlightedId(null);
        highlightTimeoutRef.current = null;
      }, HIGHLIGHT_DURATION);
      bsMgr.SetPendingScrollTarget(null);
      pendingRafRef.current = null;
    });

    return () => {
      if (pendingRafRef.current != null) {
        cancelAnimationFrame(pendingRafRef.current);
        pendingRafRef.current = null;
      }
    };
  }, [sortedBoxStyles, pendingEditTick, bsMgr]);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current != null) {
        clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = null;
      }
    };
  }, []);

  if (!bsMgr || !ctMgr) return null;

  return (
    <Flex direction={"column"} height={"100%"} width={"100%"} className="ocn-box-styles-panel">
      <Flex direction={"column"} gap="16px" padding="16px" className="ocn-box-styles-header">
        <Text size="small">
          {appstr("Create and manage box styles for your charts. Apply box styles with a View.")}
        </Text>
        {canUpdateViews && (
          <Flex>
            {isPositionChart || canAccessGrouping ? (
              <Menu
                trigger={
                  <Button
                    modifier="secondary"
                    iconAlignEnd
                    iconProps={{ name: "caret-down" }}
                    dataTestId="boxstyles--create"
                  >
                    {appstr("Create box style")}
                  </Button>
                }
                options={createOptions}
                position={"bottom-start"}
                listWidth="medium"
              />
            ) : (
              <Button
                modifier="secondary"
                iconProps={{ name: "plus" }}
                dataTestId="boxstyles--create"
                onClick={() => handleCreate(enumHierarchyTypes.employees)}
              >
                {appstr("Create box style")}
              </Button>
            )}
          </Flex>
        )}
        <Flex gap="16px" alignItems="center">
          <Search
            {...searchProps}
            placeholder={appstr("Search box styles")}
            dataTestId={chartModeTestId("sidebar", "box-styles-search")}
          />
          <Popover
            openCtrlRef={filterCtrlRef}
            options={{ placement: "bottom-end" }}
            reference={filterTrigger}
          >
            <BoxStyleFilters
              activeFilters={activeFilters}
              toggleFilter={toggleFilter}
              onResetFilters={onResetFilters}
              isPositionChart={isPositionChart}
              canAccessGrouping={canAccessGrouping}
            />
          </Popover>
          <Menu
            trigger={sortTrigger}
            options={sortOptions}
            stayOpenOnOptionSelect
            value={sortBy}
            showCheck
            position={"bottom-end"}
            dataTestId={chartModeTestId("sidebar", "box-styles-sort-btn")}
          />
        </Flex>
      </Flex>

      <PendingEditsBanner changeCount={pendingEditCount} deleteCount={tombstoneCount} />

      {displayBoxStyles.length === 0 ? (
        <Flex justifyContent="center" alignItems="center">
          <Text colorModifier="grey" size="small">
            {appstr("nomatches")}
          </Text>
        </Flex>
      ) : (
        <Flex direction={"column"} gap="16px" className="ocn-box-styles-list ocn-list-scrollbar">
          {displayBoxStyles.map(boxStyle => {
            const id = boxStyle.layout.GetBoxStyleID();
            const hasPendingEdit = bsMgr.HasEditDraft(id);
            const liveDraft =
              id === currentEditingId ? wsMgr?.GetBLELayout?.() : bsMgr.GetEditDraft(id)?.layout;
            return (
              <div
                key={id}
                className={id === highlightedId ? "ocn-box-style-summary-scroll-target" : undefined}
                ref={element => {
                  itemRefs.current[id] = element;
                }}
              >
                <BoxStyleSummary
                  boxStyleSet={boxStyle.boxStyleSet}
                  layout={boxStyle.layout}
                  previewLayout={liveDraft ?? undefined}
                  previewVersion={id === currentEditingId ? pendingEditTick : 0}
                  viewCount={boxStyleCounts.get(id) || 0}
                  hasPendingEdit={hasPendingEdit}
                  isActive={id === currentEditingId}
                  getViewTemplates={id => ctMgr.GetTemplatesForBoxStyle(id)}
                  onViewNavigate={handleViewNavigate}
                  onCopy={handleCopy}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onRevert={handleRevert}
                  onRename={handleRename}
                  readOnly={!canUpdateViews}
                />
              </div>
            );
          })}
        </Flex>
      )}
    </Flex>
  );
};

export default BoxStyles;
